import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { CircularProgress, InputAdornment, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';

import { client } from 'src/utils/api-client';
import { getFlatData } from './utils';

import { StyledPanel, StyledTitle } from 'src/Superadmin/index.styles';
import { StyledFlex } from 'src/components/StyledFlex';
import PageLoading from 'src/components/PageLoading';
import SomethingWentWrong from 'src/components/SomethingWentWrong';
import DelayedTextField from 'src/components/DelayedTextField';
import ElementsPagination from 'src/components/Pagination';
import { useElementsPagination } from 'src/common/hooks';
import { useBranchesWithCountry } from 'src/utils/branches';
import CountryPicker, {
  CountryPickerOption,
} from 'src/components/CountryPicker';
import StructureTable, { NO_COUNTRY_VALUE } from './StructureTable';
import { OrganizationTreeNodeSchema } from '../../../components/StructureTable/index.types';
import { ContentProps } from '../index.types';
import { usePlatform } from 'src/redux-file/hooks';
import MaxOrgReachedAlert from './MaxOrgReachedAlert';

// Sentinel used by StructureTable's client-side column narrowing when the
// country-multiselect-picker (specs/001-raben-country-masteradmin/
// mass-specs/specs/20260826-country-multiselect-picker.md) doesn't resolve
// to exactly one country -- kept distinct from NO_COUNTRY_VALUE ('none',
// which means "branches with no country").
const ALL_COUNTRIES_VALUE = '';

// eslint-disable-next-line no-unused-vars
export default function Structure(props: ContentProps) {
  const { t } = useTranslation();
  const { organizations, platformSubscriber } = usePlatform();
  const isMaxOrgReached =
    (organizations || []).length >= platformSubscriber.maxNumOrganizations;

  const { branches: allBranches } = useBranchesWithCountry();

  const [search, setSearch] = useState('');
  // country-multiselect-picker (R-19): replaces the old single-value
  // `country` string with a list of ISO codes (BR-2/BR-3) -- EC-5 requires
  // sending no `countries` param at all when this is empty, never `[]`.
  const [countries, setCountries] = useState<string[]>([]);
  const { page, setPage, elementsPerPage, setElementsPerPage } =
    useElementsPagination(1, 20);

  // C09's `total` (count of matching carriers) rides alongside the node
  // tree in the API response, but the query itself keeps returning the bare
  // nodes array -- exactly like before pagination existed -- so Row.tsx's
  // getQueryData/setQueryData optimistic-update cache access (keyed on this
  // same 'organization-structure' string) keeps working unmodified.
  const [total, setTotal] = useState(0);

  // EC-1 / DECISIONS-RESOLVED "C09 §1": retry:false so a permanent 403
  // settles into the error state immediately instead of retrying 3 times.
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    OrganizationTreeNodeSchema[],
    AxiosError<{ detail?: string }>
  >(
    'organization-structure',
    () =>
      client
        .get<{ nodes: OrganizationTreeNodeSchema[]; total?: number }>(
          '/web/settings_v2/organizations/tree',
          {
            params: {
              search: search.trim() || undefined,
              // C10 interface contract: `countries` (repeated), not the old
              // singular `country` -- EC-5, an empty selection must send no
              // key at all rather than `countries=[]`.
              countries: countries.length ? countries : undefined,
              page,
              page_size: elementsPerPage,
            },
          }
        )
        .then((res) => {
          setTotal(res.data?.total ?? 0);
          return res.data?.nodes || [];
        }),
    { retry: false }
  );

  // Query key is deliberately static (see above, so Row.tsx's cache access
  // keeps working unmodified), so filter/pagination changes need an
  // explicit refetch -- skip the one on mount, useQuery already fires the
  // initial request itself.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, countries, page, elementsPerPage]);

  const flatData = useMemo(() => getFlatData(data || []), [data]);

  // StructureTable's own column narrowing (Goal 1 of fe-structure-country.md)
  // pre-dates multi-select and only ever narrowed to ONE country -- keep
  // that contract exactly as-is (narrow only when the picker resolves to
  // exactly one country; otherwise show every group, same as "all
  // countries" did before this component existed). The country-folding
  // feature (fb029efc2) is what actually keeps a wide, unnarrowed column
  // set manageable now, so this deliberately doesn't try to widen
  // StructureTable's own narrowing to a list.
  const narrowingCountry =
    countries.length === 1 ? countries[0] : ALL_COUNTRIES_VALUE;

  const handleSearchChange = (value: string) => {
    setPage(1);
    setSearch(value);
  };

  const handleCountriesChange = (codes: string[]) => {
    setPage(1);
    setCountries(codes);
  };

  const countryOptions = useMemo(() => {
    const seen = new Map<string, string>();
    let hasNoCountry = false;
    allBranches.forEach((branch) => {
      if (branch.country_code) {
        if (!seen.has(branch.country_code)) {
          seen.set(
            branch.country_code,
            branch.country_name || branch.country_code
          );
        }
      } else {
        hasNoCountry = true;
      }
    });
    return {
      countries: Array.from(seen, ([code, name]) => ({ code, name })),
      hasNoCountry,
    };
  }, [allBranches]);

  const noCountryLabel = String(
    t('superadmin.settings.tabs.structure.filters.noCountry')
  );

  // EC-2 (countries=[PL, none] on the tree): the "Bez kraju" bucket is a
  // selectable pseudo-country here (Structure's tree endpoint has a real
  // `none` bucket to query for -- Users/organization-members does not, see
  // that screen's own comment). `summaryLabel` keeps the closed control
  // showing the translated "No country" text instead of the literal wire
  // value "none" for this one entry, matching what the old single-select
  // always showed here.
  const pickerOptions: CountryPickerOption[] = useMemo(() => {
    const opts: CountryPickerOption[] = countryOptions.countries.map((c) => ({
      code: c.code,
      name: c.name,
    }));
    if (countryOptions.hasNoCountry) {
      opts.push({
        code: NO_COUNTRY_VALUE,
        name: noCountryLabel,
        summaryLabel: noCountryLabel,
      });
    }
    return opts;
  }, [countryOptions, noCountryLabel]);

  const status = error?.response?.status;
  const permissionDenied = status === 403;

  // Goal 4: distinct loading / error / empty / data states. Built as an
  // if/else chain (not nested ternaries) purely to keep the JSX below flat.
  let content: React.ReactNode;
  if (isLoading) {
    content = <PageLoading />;
  } else if (isError) {
    // NOTE: in the real app this branch is a defensive fallback, not the
    // primary path a user sees for a 403. `client`'s shared axios instance
    // has a global response interceptor (AuthorizedAccessProvider.tsx) that
    // intercepts a 403 first and navigates to `/403`, which renders
    // <Forbidden /> (registered in UnauthorizedAccessProvider.tsx, which
    // wraps AuthorizedAccessProvider -- see App.tsx) -- so this component
    // typically unmounts before this render would ever be seen. It still
    // renders a correct, permission-specific state here so the component is
    // right on its own terms: whenever the query surfaces a 403 without
    // that global redirect in play (e.g. this component under test in
    // isolation, or if that interceptor's behaviour ever changes), the
    // ghost-table bug does not reappear.
    content = (
      <SomethingWentWrong
        errorMessege={
          permissionDenied
            ? String(
                t('superadmin.settings.tabs.structure.states.permissionError')
              )
            : undefined
        }
      />
    );
  } else if (total === 0) {
    content = (
      <StyledPanel sx={{ mt: '24px', minWidth: '1050px' }}>
        <Typography align="center" color="text.secondary">
          {t('superadmin.settings.tabs.structure.states.empty')}
        </Typography>
      </StyledPanel>
    );
  } else {
    content = (
      <>
        <StyledPanel sx={{ mt: '24px', minWidth: '1050px' }}>
          <StructureTable data={flatData} country={narrowingCountry} />
        </StyledPanel>
        <ElementsPagination
          totalElements={total}
          elementsPerPage={elementsPerPage}
          setElementsPerPage={setElementsPerPage}
          page={page}
          setPage={setPage}
          sx={{ mt: '16px' }}
        />
      </>
    );
  }

  return (
    <div>
      <StyledFlex>
        <StyledTitle sx={{ fontSize: '28px' }}>
          {t('superadmin.settings.tabs.structure.title')}
        </StyledTitle>
      </StyledFlex>
      <br />

      {isMaxOrgReached && <MaxOrgReachedAlert />}

      <StyledFlex
        sx={{ justifyContent: 'flex-start', gap: '16px', mb: '16px' }}
      >
        <DelayedTextField
          onDelayedChange={handleSearchChange}
          placeholder={String(
            t('superadmin.settings.tabs.structure.searchText')
          )}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: '360px' }}
        />
        <CountryPicker
          options={pickerOptions}
          value={countries}
          onChange={handleCountriesChange}
          ariaLabel={String(
            t('superadmin.settings.tabs.structure.filters.countryLabel')
          )}
          allSelectedLabel={String(
            t('superadmin.settings.tabs.structure.filters.allCountries')
          )}
          searchPlaceholder={String(
            t('superadmin.settings.countryPicker.searchPlaceholder')
          )}
          noResultsText={String(
            t('superadmin.settings.countryPicker.noResults')
          )}
          selectedSummaryTemplate={String(
            t('superadmin.settings.countryPicker.selectedSummary')
          )}
          sx={{ minWidth: '180px' }}
        />
        {isFetching && !isLoading && <CircularProgress size={20} />}
      </StyledFlex>

      {content}
    </div>
  );
}
